import React, { useEffect, useMemo, useState } from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useDispatch, useSelector } from 'react-redux';
import { MapView } from '../mapView/MapView';
import { FancyIconButton } from '../common/buttons';
import { FilterButton } from '../header/buttons/FilterButton';
import { Search } from '../header/filters/search/Search';
import { setDialogOpen, setSelectedEvent, setSelectedGroup } from '../../store/Actions';
import './TimelineView.scss';

const DATE_GROUP_OPTIONS = [
  { id: 'day', label: 'Day' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];

const toDate = (value) => value instanceof Date ? value : value.toDate();

const getEventStartAt = (event) => {
  const startAt = toDate(event.startDate);
  const startTime = event.startTime ? toDate(event.startTime) : null;
  if(startTime){
    startAt.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
  }
  return startAt;
};

const sortEventsNewestFirst = (events) => [...events].sort((a, b) => getEventStartAt(b) - getEventStartAt(a));

const sortEvents = (events, sortMode) => [...events].sort((a, b) => {
  if(sortMode === 'oldest') return getEventStartAt(a) - getEventStartAt(b);
  return getEventStartAt(b) - getEventStartAt(a);
});

const getGroupDomId = (groupId) => `timeline-group-${String(groupId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const formatGroupDate = (events) => {
  if(events.length === 0) return '';

  const sortedEvents = sortEventsNewestFirst(events);
  const newest = getEventStartAt(sortedEvents[0]);
  const oldest = getEventStartAt(sortedEvents[sortedEvents.length - 1]);
  const sameYear = newest.getFullYear() === oldest.getFullYear();

  if(sameYear) return `${newest.getFullYear()}`;
  return `${oldest.getFullYear()} - ${newest.getFullYear()}`;
};

const formatEventDate = (event) => {
  const date = getEventStartAt(event);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatEventTime = (event) => {
  if(!event.startTime) return null;
  const date = getEventStartAt(event);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// For each event, compute the {id, label, sortValue, ...} of the date
// bucket it belongs to under the current granularity (day / month / year).
const getDateGroupParts = (event, dateGranularity) => {
  const date = getEventStartAt(event);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const dayLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if(dateGranularity === 'day'){
    return {
      id: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      label: dayLabel,
      sortValue: new Date(year, month, day).getTime(),
      railLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      railDetail: String(year),
    };
  }

  if(dateGranularity === 'month'){
    return {
      id: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: monthLabel,
      sortValue: new Date(year, month, 1).getTime(),
      railLabel: date.toLocaleDateString('en-US', { month: 'short' }),
      railDetail: String(year),
    };
  }

  return {
    id: String(year),
    label: String(year),
    sortValue: year,
    railLabel: String(year),
    railDetail: '',
  };
};

const addGroup = (groups, id, label, event, meta = {}) => {
  if(!id || !label) return;
  if(!groups[id]){
    groups[id] = { id, label, events: [], ...meta };
  }
  groups[id].events.push(event);
};

const getCategoryCounts = (events) => {
  const counts = {};
  events.forEach(event => {
    counts[event.categoryId] = (counts[event.categoryId] || 0) + 1;
  });
  return counts;
};

// Build the per-bucket groups for the active date granularity. Groups are
// returned sorted (newest or oldest first) and each group's events are
// sorted the same way.
const buildGroups = ({dateGranularity, events, sortMode}) => {
  const groups = {};

  events.forEach(event => {
    const dateGroup = getDateGroupParts(event, dateGranularity);
    addGroup(groups, dateGroup.id, dateGroup.label, event, {
      sortValue: dateGroup.sortValue,
      railLabel: dateGroup.railLabel,
      railDetail: dateGroup.railDetail,
    });
  });

  return Object.values(groups)
    .map(group => ({
      ...group,
      events: sortEvents(group.events, sortMode),
      startAt: group.events.reduce((latest, event) => {
        const startAt = getEventStartAt(event);
        return !latest || startAt > latest ? startAt : latest;
      }, null),
    }))
    .sort((a, b) => {
      if(sortMode === 'oldest') {
        if(a.sortValue && b.sortValue) return a.sortValue - b.sortValue;
        if(a.startAt && b.startAt) return a.startAt - b.startAt;
        return a.label.localeCompare(b.label);
      }
      if(a.sortValue && b.sortValue) return b.sortValue - a.sortValue;
      if(a.startAt && b.startAt) return b.startAt - a.startAt;
      return a.label.localeCompare(b.label);
    });
};

function TimelineGroupHeader({group, dateGranularity, categories}) {
  const categoryCounts = getCategoryCounts(group.events);
  const sortedCategoryIds = Object.keys(categoryCounts).sort((a, b) => categoryCounts[b] - categoryCounts[a]);
  // Year buckets get a per-category breakdown next to the count; finer
  // granularities (month / day) skip that since the count would be lower
  // signal-to-noise.
  const showCategoryCounts = dateGranularity === 'year';
  // For day/month buckets we tack on a friendly "Mar 5 - Mar 12" range so
  // the user can see exactly which dates the bucket covers.
  const showRange = dateGranularity !== 'year';

  return (
    <div className="new-timeline-group__header">
      <div className="new-timeline-group__header-body">
        <div className="new-timeline-group__title-row">
          <div className="new-timeline-group__title">{group.label}</div>
        </div>
        <div className="new-timeline-group__meta">
          <span>{group.events.length} {group.events.length === 1 ? 'event' : 'events'}</span>
          {showRange &&
            <span>{formatGroupDate(group.events)}</span>
          }
        </div>
        {showCategoryCounts &&
          <div className="new-timeline-group__category-counts">
            {sortedCategoryIds.map(categoryId => {
              const category = categories?.[categoryId];
              if(!category) return null;
              return (
                <div className="new-timeline-group__category-count" key={categoryId}>
                  <span style={{backgroundColor: category.pinColor}}>{category.defaultEmoji}</span>
                  <strong>x{categoryCounts[categoryId]}</strong>
                </div>
              );
            })}
          </div>
        }
      </div>
    </div>
  );
}

function TimelineDateRail({groups, activeGroupId, onSelectGroup}) {
  if(groups.length === 0) return null;

  return (
    <div className="new-timeline-date-rail" aria-label="Date timeline">
      <div className="new-timeline-date-rail__track">
        <span className="new-timeline-date-rail__cap new-timeline-date-rail__cap--start" aria-hidden="true"/>
        {groups.map(group => (
          <button
            key={group.id}
            type="button"
            className={group.id === activeGroupId ? 'is-active' : ''}
            onClick={() => onSelectGroup(group.id)}
          >
            <span className="new-timeline-date-rail__dot"/>
            <span className="new-timeline-date-rail__label">{group.railLabel || group.label}</span>
            {group.railDetail &&
              <span className="new-timeline-date-rail__detail">{group.railDetail}</span>
            }
            <strong>{group.events.length} {group.events.length === 1 ? 'event' : 'events'}</strong>
          </button>
        ))}
        <span className="new-timeline-date-rail__cap new-timeline-date-rail__cap--end" aria-hidden="true"/>
      </div>
    </div>
  );
}

function CompactEventRow({event, categories, trips, friends, tags, onOpenEvent}) {
  const category = categories?.[event.categoryId];
  const tripNames = (event.tripIds || []).map(tripId => trips?.[tripId]?.name).filter(Boolean);
  const friendNames = (event.friendIds || []).map(friendId => friends?.[friendId]?.name).filter(Boolean);
  const tagNames = (event.tagIds || []).map(tagId => tags?.[tagId]?.name).filter(Boolean);
  const metadata = [
    category?.name,
    tripNames.length > 0 ? tripNames.join(', ') : '',
    friendNames.length > 0 ? friendNames.join(', ') : '',
    tagNames.length > 0 ? tagNames.join(', ') : '',
  ].filter(Boolean);

  const eventTime = formatEventTime(event);

  return (
    <button className="new-timeline-compact-event" type="button" id={event.id} onClick={() => onOpenEvent(event)}>
      <div className="new-timeline-compact-event__date">
        <span>{formatEventDate(event)}</span>
        {eventTime &&
          <span className="new-timeline-compact-event__time">{eventTime}</span>
        }
      </div>
      <div className={`new-timeline-compact-event__cover ${event.coverPicUrl ? 'has-image' : ''}`} style={{backgroundColor: category?.pinColor || '#e5e7eb'}}>
        {event.coverPicUrl &&
          <img src={event.coverPicUrl} alt="" loading="lazy"/>
        }
        {!event.coverPicUrl &&
          <span className="new-timeline-compact-event__marker">
            {event.emoji || category?.defaultEmoji}
          </span>
        }
      </div>
      <div className="new-timeline-compact-event__body">
        <div className="new-timeline-compact-event__title">{event.title}</div>
        <div className="new-timeline-compact-event__location">{event.location?.description}</div>
        {metadata.length > 0 &&
          <div className="new-timeline-compact-event__meta">{metadata.join(' · ')}</div>
        }
      </div>
    </button>
  );
}

export function NewTimelineView() {
  const [dateGranularity, setDateGranularity] = useState('year');
  const [sortMode, setSortMode] = useState('newest');
  const [activeGroupId, setActiveGroupId] = useState(null);
  const filteredEvents = useSelector(state=>state.filteredEvents);
  const selectedEvent = useSelector(state=>state.selectedEvent);
  const categories = useSelector(state=>state.viewingUserCategories);
  const trips = useSelector(state=>state.viewingUserTrips);
  const friends = useSelector(state=>state.viewingUserFriends);
  const tags = useSelector(state=>state.viewingUserTags);
  const appWidth = useSelector(state=>state.appWidth);
  const mapVisible = appWidth > 980;
  const eventList = useMemo(() => sortEventsNewestFirst(filteredEvents ? Object.values(filteredEvents) : []), [filteredEvents]);
  const groups = useMemo(() => buildGroups({
    dateGranularity,
    events: eventList,
    sortMode,
  }), [dateGranularity, eventList, sortMode]);
  const activeGroup = useMemo(() => {
    return groups.find(group => group.id === activeGroupId) || groups[0] || null;
  }, [activeGroupId, groups]);
  const visibleGroups = activeGroup ? [activeGroup] : [];
  const activeMapEvents = activeGroup?.events || [];
  const dispatch = useDispatch();

  const openEventDetail = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const focusEventOnMap = (event) => {
    dispatch(setSelectedEvent(event));
  };

  // First click focuses the event on the map; clicking the already-selected
  // event (or any click when the map is hidden) opens the View Event dialog.
  const handleListEventClick = (event) => {
    if(!mapVisible || selectedEvent?.id === event.id){
      openEventDetail(event);
    } else {
      focusEventOnMap(event);
    }
  };

  const selectActiveGroup = (groupId) => {
    setActiveGroupId(groupId);
    dispatch(setSelectedEvent(null));
  };

  useEffect(() => {
    if(groups.length > 0 && !groups.some(group => group.id === activeGroupId)){
      setActiveGroupId(groups[0].id);
      dispatch(setSelectedEvent(null));
    }
  }, [activeGroupId, dispatch, groups]);

  useEffect(()=>{
    if(selectedEvent){
      dispatch(setSelectedGroup(getEventStartAt(selectedEvent).getFullYear()));
      const eventGroup = groups.find(group => group.events.some(groupEvent => groupEvent.id === selectedEvent.id));
      if(eventGroup && eventGroup.id !== activeGroupId){
        setActiveGroupId(eventGroup.id);
      }
    }
  },[dispatch, selectedEvent, groups, activeGroupId]);

  return (
    <div className="new-timeline-view">
      <section className="new-timeline-workspace">
        <header className="new-timeline-toolbar">
          <div className="new-timeline-toolbar__controls">
            <div className="new-timeline-search-tools">
              <div className="new-timeline-search-tools__search">
                <Search/>
              </div>
              <select
                className="new-timeline-date-control"
                aria-label="Group dates by"
                value={dateGranularity}
                onChange={(event) => {
                  setDateGranularity(event.target.value);
                  setActiveGroupId(null);
                  dispatch(setSelectedEvent(null));
                }}
              >
                {DATE_GROUP_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              <div className="new-timeline-search-tools__filter">
                <FilterButton/>
              </div>
              <FancyIconButton
                variant={sortMode === 'newest' ? 'primary' : 'secondary'}
                size="md"
                shape="circle"
                ariaLabel={sortMode === 'newest' ? 'Sort oldest first' : 'Sort newest first'}
                title={sortMode === 'newest' ? 'Newest first' : 'Oldest first'}
                className="new-timeline-sort-icon"
                onClick={() => setSortMode(sortMode === 'newest' ? 'oldest' : 'newest')}
              >
                <SwapVertIcon/>
              </FancyIconButton>
            </div>
          </div>
          <TimelineDateRail
            groups={groups}
            activeGroupId={activeGroup?.id}
            onSelectGroup={selectActiveGroup}
          />
        </header>
        <div className="new-timeline-content">
          <div className="new-timeline-list">
            {visibleGroups.length === 0 &&
              <div className="new-timeline-empty">No events match the current filters.</div>
            }
            {visibleGroups.map(group => (
              <section className="new-timeline-group" id={getGroupDomId(group.id)} key={group.id}>
                <TimelineGroupHeader
                  group={group}
                  dateGranularity={dateGranularity}
                  categories={categories}
                />
                <div className="new-timeline-group__events">
                  {group.events.map(event => (
                    <CompactEventRow
                      key={`${group.id}-${event.id}`}
                      event={event}
                      categories={categories}
                      trips={trips}
                      friends={friends}
                      tags={tags}
                      onOpenEvent={handleListEventClick}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
      <aside className="new-timeline-map">
        <MapView hideSidePanel={true} eventsOverride={activeMapEvents} onMarkerClick={openEventDetail}/>
      </aside>
    </div>
  );
}
